/**
 * Add or edit a provider's key from Settings → Models, one dialog for every
 * row that takes one: the named third parties, "Other API" and an Ollama
 * server, whose key is for a proxy in front of it and optional. The key is
 * checked server-side (it lists the account's models) before it is stored, so
 * a bad key comes back as a message under the field rather than a silent
 * failure, and re-typing it is the only way to change one — the field never
 * shows the stored value back, only its hint.
 */

import { useState } from 'react';
import { ApiError, deleteProvider, putProvider, type ProviderStatus } from '../api';
import { ds } from '../ds';
import { useApp } from '../state';

export function ProviderDialog({
  status,
  onClose,
  onSaved
}: {
  status: ProviderStatus;
  onClose: () => void;
  /** The row changed on the server; refetch the list. */
  onSaved: () => void;
}) {
  const { Dialog, Button, Input } = ds();
  const { refreshModels } = useApp();
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState(status.baseUrl ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOllama = status.provider === 'ollama';
  const addressed = isOllama || status.provider === 'custom';
  // Ollama's key is optional, but one already stored is typed again to keep
  // it, as every key is: saving without it would drop it.
  const needsKey = !isOllama || !!status.keyHint;
  const ready = (!needsKey || !!apiKey.trim()) && (!addressed || !!baseUrl.trim());
  const done = () => {
    onSaved();
    void refreshModels();
    onClose();
  };

  const save = () => {
    if (busy || !ready) return;
    setBusy(true);
    setError(null);
    putProvider(status.provider, { apiKey: apiKey.trim(), ...(addressed ? { baseUrl: baseUrl.trim() } : {}) })
      .then(done)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'That did not go through. Try again.'))
      .finally(() => setBusy(false));
  };

  const remove = () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    deleteProvider(status.provider)
      .then(done)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'That did not go through. Try again.'))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog
      open
      position="fixed"
      width={420}
      title={status.name}
      onClose={onClose}
      style={{ borderRadius: 'var(--radius-dialog)', padding: 24, gap: 16 }}
      footer={
        <>
          {status.state === 'connected' ? (
            <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={remove}>
              {isOllama ? 'Remove server' : 'Remove key'}
            </Button>
          ) : null}
          <span style={{ flex: 1 }} />
          <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" density="app" size="sm" disabled={busy || !ready} onClick={save}>
            Save
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {isOllama ? (
          <Input
            label="Address"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://ollama.example.com"
            autoFocus
          />
        ) : null}
        <Input
          label={isOllama ? 'Key, if your server asks for one' : 'API key'}
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={status.keyHint ? `…${status.keyHint}` : undefined}
          autoFocus={!isOllama}
        />
        {status.provider === 'custom' ? (
          <Input
            label="Base URL"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://api.example.com/v1"
          />
        ) : null}
        {error ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>{error}</span> : null}
      </div>
    </Dialog>
  );
}
